import { PaperclipIcon } from "@phosphor-icons/react/dist/csr/Paperclip";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { ScrollArea } from "@volca/ui/components/ui/scroll-area";
import { Stack } from "@volca/ui/components/ui/stack";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { editorError } from "@/payroll/editor/state";
import { AttachmentPreview } from "./attachment-preview";
import { AttachmentTile } from "./attachment-tile";
import { enrichAttachments } from "./enrich-attachments";
import type { EditorUpload } from "./use-editor-upload";

export const EditorAttachments = ({
  documents,
  remove,
  upload,
  mode = "composer",
  sentUploadIds = [],
  pendingUploadIds = [],
}: {
  documents: PayrollEditorQueries["documents"];
  remove: PayrollEditorQueries["remove"];
  upload: EditorUpload;
  mode?: "context" | "composer";
  sentUploadIds?: string[];
  pendingUploadIds?: string[];
}) => {
  const [previewId, setPreviewId] = useState<string>();
  const files = enrichAttachments(
    documents.data ?? [],
    upload.items,
    upload.recentFiles,
  );
  const shown =
    mode === "context"
      ? files.filter(
          (file) =>
            file.submitted || sentUploadIds.includes(file.clientUploadId ?? ""),
        )
      : files.filter((file) => upload.recentIds.includes(file.id));
  const notice = mode === "context" ? documents.error : remove.error;
  const tiles = shown.map((file) => (
    <AttachmentTile
      key={file.id}
      file={file}
      disabled={!upload.available || upload.isUploading}
      onInspect={() => setPreviewId(file.id)}
      onRetry={
        file.canRetry && file.uploadId
          ? () => upload.retry(file.uploadId!)
          : undefined
      }
      onRemove={
        pendingUploadIds.includes(file.clientUploadId ?? "")
          ? undefined
          : () => {
              if (file.uploadId) void upload.discard(file.uploadId);
              else if (
                file.documentId &&
                window.confirm(`Remove ${file.name} from this proposal?`)
              )
                remove.mutate(file.documentId, {
                  onSuccess: () => upload.forgetRecent(file.id),
                });
            }
      }
    />
  ));
  const issue = !!notice && (
    <Stack gap={1} className="text-xs" role="alert">
      <span>{editorError(notice)}</span>
      <Button
        type="button"
        size="xs"
        variant="outline"
        onClick={() => {
          remove.reset();
          void documents.refetch();
        }}
      >
        Refresh files
      </Button>
    </Stack>
  );
  const preview = (
    <AttachmentPreview
      file={files.find((file) => file.id === previewId)}
      onClose={() => setPreviewId(undefined)}
    />
  );
  if (mode === "context")
    return (
      <Stack
        direction="row"
        align="center"
        justify="between"
        className="shrink-0 border-b border-line px-5 py-1"
      >
        <span className="text-xs font-medium text-subtle">Sources</span>
        <Popover>
          <PopoverTrigger
            render={
              <Button
                type="button"
                size="xs"
                variant="ghost"
                className="text-subtle"
              />
            }
          >
            <PaperclipIcon aria-hidden className="size-3" />
            {documents.isPending
              ? "Loading files…"
              : documents.isError
                ? "Files unavailable"
                : `${shown.length} ${shown.length === 1 ? "file" : "files"} added`}
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            className="w-96 max-w-[calc(100vw-2rem)]"
          >
            <PopoverTitle>Conversation files</PopoverTitle>
            {documents.isPending ? (
              <p role="status" className="text-xs text-subtle">
                Loading files…
              </p>
            ) : !shown.length && !notice ? (
              <p className="text-xs text-subtle">
                Files added to this conversation appear here.
              </p>
            ) : null}
            <ScrollArea
              className="max-h-96"
              viewportProps={{ className: "max-h-96" }}
            >
              <ul
                aria-label="Conversation files"
                className="grid grid-cols-3 gap-2 pr-2 pb-1"
              >
                {tiles}
              </ul>
            </ScrollArea>
            {issue}
            {preview}
          </PopoverContent>
        </Popover>
      </Stack>
    );
  if (!shown.length && !upload.errors.length && !notice) return null;
  return (
    <Stack gap={2} className="min-w-0 px-3 pt-3">
      {!!shown.length && (
        <ScrollArea horizontal viewportProps={{ className: "max-h-44" }}>
          <ul aria-label="New attachments" className="flex gap-2 pb-3">
            {tiles}
          </ul>
        </ScrollArea>
      )}
      {!!upload.errors.length && (
        <ul
          role="alert"
          className="max-h-24 overflow-y-auto text-xs text-danger"
        >
          {upload.errors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      )}
      {issue}
      {preview}
    </Stack>
  );
};
